import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { Button, Select } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";

const normaliseResponse = (response) => {
    if (!response?.success) {
        return [];
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }

    return [];
};

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const Agents = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();

    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const companyId = useMemo(
        () => toNumericOrNull(userData?.company_id || userData?.companyId || userData?.company),
        [userData]
    );

    const agentId = useMemo(
        () => toNumericOrNull(userData?.agent_id || userData?.agentId),
        [userData]
    );

    const childConditions = useMemo(() => {
        const conditions = {};
        
        // Priority 1: If user is an agent, show only that agent
        if (agentId) {
            conditions.id = agentId;
        }
        // Priority 2: If user belongs to a company, show all agents from that company
        else if (companyId) {
            conditions.company_id = companyId;
        }
        // Priority 3: If user has user_id, try to match by user_id in agents table
        else if (userData?.id) {
            conditions.user_id = userData.id;
        }
        
        return conditions;
    }, [companyId, agentId, userData?.id]);

    const fetchAgents = useCallback(async () => {
        if (!userData?.id) {
            setAgents([]);
            return;
        }

        setLoading(true);
        try {
            console.log("Fetching agents with conditions:", childConditions);
            const agentRes = await oneKlicks.read("", "insurance_agents", childConditions, "created_at DESC", "", "", "0,200");
            const agentRows = normaliseResponse(agentRes);
            
            // Additional client-side filtering if needed (for user_id matching)
            let filteredRows = agentRows;
            if (!agentId && !companyId && userData?.id) {
                // If no company/agent ID, filter by user_id match
                filteredRows = agentRows.filter(agent => {
                    const agentUserId = toNumericOrNull(agent.user_id || agent.userId);
                    return agentUserId === toNumericOrNull(userData.id);
                });
            }
            
            setAgents(filteredRows);
        } catch (error) {
            console.error("Error loading agents:", error);
            toast.error("Failed to load agents");
            setAgents([]);
        } finally {
            setLoading(false);
        }
    }, [childConditions, agentId, companyId, userData?.id]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    const filteredAgents = useMemo(() => {
        let filtered = agents;
        
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((agent) => {
                return (
                    agent.full_name?.toLowerCase().includes(searchLower) ||
                    agent.name?.toLowerCase().includes(searchLower) ||
                    agent.agent_email?.toLowerCase().includes(searchLower) ||
                    agent.email?.toLowerCase().includes(searchLower) ||
                    agent.agent_phone?.toLowerCase().includes(searchLower) ||
                    agent.phone?.toLowerCase().includes(searchLower) ||
                    agent.company_name?.toLowerCase().includes(searchLower) ||
                    agent.designation?.toLowerCase().includes(searchLower) ||
                    agent.license_number?.toLowerCase().includes(searchLower) ||
                    agent.agent_uid?.toLowerCase().includes(searchLower) ||
                    agent.uid?.toLowerCase().includes(searchLower)
                );
            });
        }
        
        if (statusFilter !== "all") {
            filtered = filtered.filter((agent) => (agent.status || "active").toLowerCase() === statusFilter.toLowerCase());
        }
        
        return filtered;
    }, [agents, searchText, statusFilter]);

    const getAgentInitials = (name) => {
        if (!name) return "A";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name[0].toUpperCase();
    };

    const getAgentImage = (agent) => {
        if (agent.profile_image || agent.image || agent.profile_photo_url) {
            const imageUrl = agent.profile_image || agent.image || agent.profile_photo_url;
            if (imageUrl.startsWith("http")) {
                return imageUrl;
            }
            return process.env.REACT_APP_API_BASE_URL + imageUrl;
        }
        return null;
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Agents Management Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Agents Management</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Agents</Badge>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button 
                                        className="btn-outline-primary" 
                                        onClick={fetchAgents}
                                        disabled={loading}
                                        style={{ height: '40px', borderRadius: "5px" }}
                                    >
                                        <ReloadOutlined /> Refresh
                                    </Button>
                                    {/* Only show "Add New Agent" button if user is not an agent (company admin can add agents) */}
                                    {!agentId && (
                                        <Button 
                                            className="btn-primary" 
                                            onClick={() => navigate("/insurance/agents/new")}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        >
                                            <PlusOutlined /> Add New Agent
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginBottom: '20px' }}>
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search agents..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ height: '40px', borderRadius: "5px" }}
                                        />
                                    </InputGroup>
                                </div>
                                <div style={{ minWidth: '180px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Status
                                    </label>
                                    <Select
                                        value={statusFilter}
                                        onChange={(value) => setStatusFilter(value)}
                                        style={{ width: '100%' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Status' },
                                            { value: 'active', label: 'Active' },
                                            { value: 'inactive', label: 'Inactive' },
                                            { value: 'pending', label: 'Pending' },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Agents Table */}
                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredAgents.length > 0 ? (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle" style={{ width: '100%', tableLayout: 'auto' }}>
                                        <thead>
                                            <tr style={{ whiteSpace: 'nowrap' }}>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>ID</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>Image</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>Agent Name</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>Email</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>Phone</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>Agent UID</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>Designation</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>License Number</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600' }}>Status</th>
                                                <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', width: '150px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAgents.map((agent) => {
                                                const agentImage = getAgentImage(agent);
                                                const agentName = agent.full_name || agent.name || `Agent #${agent.id}`;
                                                const agentUID = agent.agent_uid || agent.uid || `AGT-${agent.id?.toString().padStart(8, '0')}`;
                                                
                                                return (
                                                    <tr key={agent.id}>
                                                        <td data-label="ID" style={{ padding: '12px 8px', verticalAlign: 'middle' }}>{agent.id}</td>
                                                        <td data-label="Image" style={{ padding: '12px 8px', verticalAlign: 'middle' }}>
                                                            {agentImage ? (
                                                                <img 
                                                                    src={agentImage} 
                                                                    alt={agentName}
                                                                    style={{
                                                                        width: '40px',
                                                                        height: '40px',
                                                                        borderRadius: '50%',
                                                                        objectFit: 'cover',
                                                                        border: '2px solid #e0e0e0'
                                                                    }}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div 
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#e0e0e0',
                                                                    display: agentImage ? 'none' : 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '14px',
                                                                    fontWeight: '600',
                                                                    color: '#666',
                                                                    border: '2px solid #e0e0e0'
                                                                }}
                                                            >
                                                                {getAgentInitials(agentName)}
                                                            </div>
                                                        </td>
                                                        <td data-label="Agent Name" style={{ padding: '12px 8px', verticalAlign: 'middle' }}>
                                                            <div style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>
                                                                {agentName}
                                                            </div>
                                                        </td>
                                                        <td data-label="Email" style={{ padding: '12px 8px', verticalAlign: 'middle' }}>
                                                            <div style={{ fontSize: '13px', color: '#333' }}>
                                                                {agent.agent_email || agent.email || "—"}
                                                            </div>
                                                        </td>
                                                        <td data-label="Phone" style={{ padding: '12px 8px', verticalAlign: 'middle' }}>
                                                            <div style={{ fontSize: '13px', color: '#333' }}>
                                                                {agent.agent_phone || agent.phone || "—"}
                                                            </div>
                                                        </td>
                                                        <td data-label="Agent UID" style={{ padding: '12px 8px', verticalAlign: 'middle', fontSize: '13px', color: '#666', fontFamily: 'monospace' }}>
                                                            {agentUID}
                                                        </td>
                                                        <td data-label="Designation" style={{ padding: '12px 8px', verticalAlign: 'middle', fontSize: '13px', color: '#666' }}>
                                                            {agent.designation || "—"}
                                                        </td>
                                                        <td data-label="License Number" style={{ padding: '12px 8px', verticalAlign: 'middle', fontSize: '13px', color: '#666' }}>
                                                            {agent.license_number || agent.registration_number || "—"}
                                                        </td>
                                                        <td data-label="Status" style={{ padding: '12px 8px', verticalAlign: 'middle', textAlign: 'center' }}>
                                                            <span className={`badge ${
                                                                (agent.status || "active").toLowerCase() === "active"
                                                                    ? "badge-success"
                                                                    : (agent.status || "active").toLowerCase() === "inactive"
                                                                    ? "badge-danger"
                                                                    : "badge-warning"
                                                            }`} style={{ borderRadius: "5px", fontSize: '12px', padding: '4px 10px', textTransform: 'capitalize' }}>
                                                                {agent.status || "active"}
                                                            </span>
                                                        </td>
                                                        <td data-label="Action" style={{ padding: '12px 8px', verticalAlign: 'middle' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                                {/* Only show Edit/Delete if user is company admin or viewing their own record */}
                                                                {(!agentId || (agentId && agent.id === agentId)) && (
                                                                    <>
                                                                        <Button 
                                                                            href="#" 
                                                                            className="dc-update-servicesss fit-content btn-info" 
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                navigate(`/insurance/agents/edit/${agent.id}`);
                                                                            }}
                                                                            style={{ borderRadius: "5px" }}
                                                                        >
                                                                            <EditOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                            Edit
                                                                        </Button>
                                                                        {/* Only company admins can delete agents */}
                                                                        {!agentId && (
                                                                            <Button 
                                                                                href="#" 
                                                                                className="dc-update-servicesss fit-content btn-danger" 
                                                                                onClick={async (e) => {
                                                                                    e.preventDefault();
                                                                                    if (window.confirm(`Are you sure you want to delete agent ${agent.full_name || agent.name || 'this agent'}?`)) {
                                                                                        try {
                                                                                            const deleteRes = await oneKlicks.delete("", "insurance_agents", { id: agent.id });
                                                                                            if (deleteRes?.success) {
                                                                                                toast.success("Agent deleted successfully");
                                                                                                fetchAgents();
                                                                                            } else {
                                                                                                toast.error("Failed to delete agent");
                                                                                            }
                                                                                        } catch (error) {
                                                                                            console.error("Error deleting agent:", error);
                                                                                            toast.error("Failed to delete agent");
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                style={{ borderRadius: "5px" }}
                                                                            >
                                                                                <DeleteOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                                Delete
                                                                            </Button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>No agents added yet</em>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Agents;

